// Dashboard.js
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BellFill,
  CalendarCheckFill,
  ClipboardData,
  List,
} from "react-bootstrap-icons";
import { HiOutlineLogout } from "react-icons/hi";
import { IoMail } from "react-icons/io5";
import { FaCalendarAlt, FaHome, FaUsers, FaChartBar } from "react-icons/fa";

import EmployeeTabs from "../HolidayLeaveReport/EmployeeTabs";
import EmployeeReport from "../EmployeeReport/EmployeeReport";
import AttendanceForm from "../HolidayLeaveReport/AttendanceForm";
import AdminEmployeeView from "../HolidayLeaveReport/AdminEmployeeView";
import RequestStatus from "../HolidayLeaveReport/RequestStatus";
import LeavesEmployee from "../EmployeeView/LeavesEmployee";
import HolidaysEmployee from "../EmployeeView/HolidaysEmployee";
import MailRequest from "../MailRequest/MailRequest";
import StatusView from "../Status/StatusView";
import CiCo from "../CICOEntry/CiCo";
import ProfileUpdate from "../Profile/ProfileUpdate";
import RuleBook from "../RuleBook/RuleBook";
import TopNavbar from "../Template/TopNavbar";

import "bootstrap/dist/css/bootstrap.min.css";
import "../Dashboard/Dashboard.css";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const SIDEBAR_WIDTH = 250;

export default function Dashboard({ user, onLogout }) {
  const [fetchRules, setFetchRules] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("cico");

  const isMobileWidth = () => window.innerWidth < 768;
  const [isMobile, setIsMobile] = useState(isMobileWidth());
  // Sidebar: icon-only on desktop by default, hidden on mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [quote, setQuote] = useState({ content: "", author: "" });

  useEffect(() => {
    axios.get(`${API_URL}/api/quotes/random`)
      .then((res) => {
        if (res.status === 200) {
          setQuote({ content: res.data.results.containt, author: res.data.results.auther });
        }
      })
      .catch(() => {});
  }, []);

  const { role, name } = user;
  const isAdmin = role === 4;
  const isTL = role === 2;
  const isEmployee = !isAdmin && !isTL;

  useEffect(() => {
    const handleResize = () => {
      const mobile = isMobileWidth();
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const goToRequest = (id) => {
    setActiveSection("requestStatus");
    navigate(`${location.pathname}?section=requestStatus&requestId=${id}`);
  };

  const handleLogout = () => {
    onLogout();
    navigate("/");
  };

  const toggleSidebar = () => setSidebarOpen((o) => !o);

  // Nav items definition
  const navItems = [
    { key: "cico", label: "Home", icon: <FaHome size={18} /> },
    { key: "status", label: "Status", icon: <ClipboardData size={18} /> },
    ...(isAdmin
      ? [{ key: "adminemployeeView", label: "Reports", icon: <CalendarCheckFill size={18} /> }]
      : []),
    { key: "employeeTabs", label: "Employee Overview", icon: <FaUsers size={18} /> },
    ...((isAdmin || isTL)
      ? [{ key: "employeeReport", label: "Employee Report", icon: <FaChartBar size={18} /> }]
      : []),
    ...((isTL || isEmployee)
      ? [{ key: "holidaysEmployee", label: "Holidays", icon: <FaCalendarAlt size={18} /> }]
      : []),
    ...((isAdmin || isTL)
      ? [{ key: "requestStatus", label: "Request Status", icon: <BellFill size={16} /> }]
      : []),
    { key: "mailRequest", label: "Mail Request", icon: <IoMail size={18} /> },
  ];

  return (
    <div className="d-flex" style={{ minHeight: "100vh" }}>
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside
        className={`new-sidebar${sidebarOpen ? " expanded" : ""}`}
        style={{
          width: isMobile ? (sidebarOpen ? `${SIDEBAR_WIDTH}px` : "0px") : (sidebarOpen ? `${SIDEBAR_WIDTH}px` : "60px"),
        }}
      >
        {/* Logo + hamburger */}
        <div className="sidebar-header">
          <img src="/protoveclogo.png" alt="Protovec" className="sidebar-logo-img" />
          <button className="sidebar-hamburger" onClick={toggleSidebar} aria-label="Toggle menu">
            <List size={22} />
          </button>
        </div>

        {/* Nav items */}
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              className={`sidebar-nav-item ${activeSection === item.key ? "active" : ""}`}
              onClick={() => {
                setActiveSection(item.key);
                if (isMobile) setSidebarOpen(false);
              }}
              title={!sidebarOpen ? item.label : undefined}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className="sidebar-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Logout */}
        <div className="sidebar-footer">
          <button className="sidebar-nav-item sidebar-logout" onClick={handleLogout} title={!sidebarOpen ? "Logout" : undefined}>
            <span className="sidebar-nav-icon"><HiOutlineLogout size={18} /></span>
            <span className="sidebar-nav-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <div
        className="main-wrapper"
        style={{
          marginLeft: isMobile ? 0 : sidebarOpen ? `${SIDEBAR_WIDTH}px` : "60px",
          transition: "margin-left 0.3s ease",
          flex: 1,
          minWidth: 0,
        }}
      >
        <TopNavbar
          navbarTitle={isAdmin ? "Admin Dashboard" : isTL ? "TL Dashboard" : "Employee Dashboard"}
          isAdmin={isAdmin}
          isTL={isTL}
          user={user}
          goToRequest={goToRequest}
          setActiveSection={setActiveSection}
          fetchRules={fetchRules}
          setFetchRules={setFetchRules}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
          sidebarOpen={sidebarOpen}
        />

        <div style={{ minHeight: "calc(100vh - 56px)" }}>
          {activeSection === "attendanceForm" && <AttendanceForm />}
          {activeSection === "adminemployeeView" && <AdminEmployeeView />}
          {activeSection === "employeeTabs" && <EmployeeTabs />}
          {activeSection === "employeeReport" && <EmployeeReport />}
          {activeSection === "requestStatus" && <RequestStatus />}
          {activeSection === "mailRequest" && <MailRequest />}
          {activeSection === "leavesEmployee" && <LeavesEmployee />}
          {activeSection === "holidaysEmployee" && <HolidaysEmployee />}
          {activeSection === "cico" && <CiCo quote={quote} />}
          {activeSection === "status" && <StatusView />}
          {activeSection === "ruleBook" && (
            <RuleBook user={user} fetchRules={fetchRules} setFetchRules={setFetchRules} />
          )}
          {activeSection === "profileUpdate" && <ProfileUpdate user={user} />}
        </div>
      </div>
    </div>
  );
}
